import { useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { colors } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import { hexToRgb, rgbToHex } from '@mui/material/styles'
import ColorizeIcon from '@mui/icons-material/Colorize'

import colorPaletteIconUrl from '@/assets/color_palette.svg?url'

import { OnboardingContext } from '@/context/OnboardingContext'
import { getPaletteColors, rgba } from '@/helpers/colorsUtil'
import { theme } from '@/context/ThemeContext'

import ColorPicker from './ColorPicker'
import { buildPaletteFromSwatch } from '@/helpers/buildPaletteFromSwatch'
import { useTemplateColors } from '../../../../hooks/useOnboarding'

import ArrowLeft from '@/assets/ArrowLeft'
import ArrowRight from '@/assets/ArrowRight'
import useIsMobile from '../../../../hooks/useIsMobile'

const ARROW_ICON_ENABLED = '#1A1A1A'
const ARROW_ICON_DISABLED = '#808080'

const colorPagerArrowIconButtonSx = {
  p: '4px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&.Mui-disabled': {
    opacity: 1,
  },
}

const colorPagerArrowRightIconButtonSx = {
  ...colorPagerArrowIconButtonSx,
  pr: '4px',
}

const MAX_PALETTES = 12
const PAGE_LIMIT = 6

const WizardStep2 = () => {
  const { selectedPalette, setSelectedPalette, templateStyle, selectedTemplate, getDefaultSwatch } = useContext(OnboardingContext)
  const { data: templateColorsData, isLoading: templateColorsisLoading } = useTemplateColors(selectedTemplate?.id)
  const [showCustomColors, setShowCustomColors] = useState(false)
  const [paletteColors, setPaletteColors] = useState({})
  const [colorPickerFor, setColorPickerFor] = useState()
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null)
  const [prevColorPalette, setPrevColorPalette] = useState({})
  const [templateColors, setTemplateColors] = useState([])
  const [currentPage, setCurrentPage] = useState(1)

  const { isMobile } = useIsMobile()
  const templateVersion = selectedTemplate?.version

  const templateColorsMapping = [
    {
      template: 'modern',
      primary:'complementary',
      secondary: 'textAlt',
      tertiary: 'brand',
      accent: 'altBrand',
      complementary: 'text'
    },
    {
      template: 'classic',
      primary:'complementary',
      secondary: 'brand',
      tertiary: 'textAlt',
      accent: 'altBrand',
      complementary: 'text'
    },
    {
      template: 'playful',
      primary:'complementary',
      secondary: 'brand',
      tertiary: 'text',
      accent: 'altBrand',
      complementary: 'textAlt'
    },
    {
      template: 'bold',
      primary:'text',
      secondary: 'complementary',
      tertiary: 'textAlt',
      accent: 'altBrand',
      complementary: 'brand'
    },
    {
      template: 'professional',
      primary:'altBrand',
      secondary: 'brand',
      tertiary: 'complementary',
      accent: 'text',
      complementary: 'textAlt'
    }
  ]
  
  const selectedMapping = templateColorsMapping.find(
    (mapping) => mapping.template === templateStyle
  );
  const listColors = useMemo(() => {
    if (!paletteColors || !templateStyle) return [];

    if (!selectedMapping) return [];

    return [
      {
        name: 'Primary',
        description: 'Main theme color, used throughout the site.',
        background: templateVersion === 'v2' ? rgba(paletteColors[selectedMapping.primary]) : rgba(paletteColors.brand),
        bind: templateVersion === 'v2' ? selectedMapping.primary : 'brand',
      },
      {
        name: 'Secondary',
        description: 'This color used for contrast as second theme color.',
        background: templateVersion === 'v2' ? rgba(paletteColors[selectedMapping.secondary]) : rgba(paletteColors.altBrand),
        bind: templateVersion === 'v2' ? selectedMapping.secondary : 'altBrand',
      },
      {
        name: 'Text',
        description: 'Default text color used throughout the site.',
        background: templateVersion === 'v2' ? rgba(paletteColors[selectedMapping.tertiary]) : rgba(paletteColors.text),
        bind: templateVersion === 'v2' ? selectedMapping.tertiary : 'text',
      },
      {
        name: 'Accent',
        description: 'A color used sparingly to highlight key elements.',
        background: templateVersion === 'v2' ? rgba(paletteColors[selectedMapping.accent]) : rgba(paletteColors.textAlt),
        bind: templateVersion === 'v2' ? selectedMapping.accent : 'textAlt',
      },
      {
        name: 'Complementary',
        description: 'A color used for depth, often in backgrounds.',
        background: templateVersion === 'v2' ? rgba(paletteColors[selectedMapping.complementary]) : rgba(paletteColors.complementary),
        bind: templateVersion === 'v2' ? selectedMapping.complementary : 'complementary',
      }
    ];
  }, [paletteColors, templateStyle]);

  /*
  Mappings with figma,
  {
    primary: primary,
    secondary: secondary,
    textPrimary: tertiary,
    textSecondary: accent,
    complementary: complementary,
  }
*/

  const createPaletteFromCustomColor = (title, theme, index = 'custom') =>
    buildPaletteFromSwatch(title, theme, index)

  const attachCustomPalettes = () => {
    const palettesToAttach = []
    //attach default swatch first
    // const defaultTheme = getDefaultSwatch(templateStyle)
    const defaultTheme = getDefaultSwatch(templateStyle, selectedTemplate?.version)
    
    const defaultPalette = createPaletteFromCustomColor('default', defaultTheme)

    if (templateVersion === 'v1') {
      palettesToAttach.push(defaultPalette)
    }
    
    // const customTheme = SWATCHES[templateStyle][templateVersion].swatches
    // customTheme?.forEach((theme, index) => {
    //   const palette = createPaletteFromCustomColor(`custom-${index}`, theme)
    //   palettesToAttach.push(palette)
    // })

    if (palettesToAttach.length >= MAX_PALETTES) {
      setTemplateColors(palettesToAttach.slice(0, MAX_PALETTES))
    } else {
      const remainingPalettes = templateColorsData
        // .filter(palette => !EXCLUDED_SWATCHES[palette.templateIndex])
        .slice(templateVersion === "v1"? 1: 0, MAX_PALETTES)

      setTemplateColors([...palettesToAttach, ...remainingPalettes])
    }
    
    return templateVersion === "v1"? palettesToAttach[0]: templateColorsData[0]
  }

  useEffect(() => {
    if (templateColorsData) {
      if (!selectedPalette) {
        const defaultPalette = attachCustomPalettes()
        setSelectedPalette(defaultPalette)
        return
      }
  
      if (selectedPalette) {
        setShowCustomColors(selectedPalette.title.toLowerCase() === 'custom')
  
        const {
          styles: { preColor: palette },
        } = selectedPalette;
  
        setPaletteColors(palette);
      }
      
      attachCustomPalettes()
    }
  }, [selectedPalette, templateColorsData]); // Keep the dependencies

  if (templateColorsisLoading || !templateColors) {
    return <CircularProgress />
  }

  const handleChange = (index) => {
    setSelectedPalette(templateColors[index])
  }

  return (
    <Box>
      <Box component={'section'}>
        <Box className="flex items-center">
          <Typography variant='h5' sx={{ mb: '6px', color: '#1A1A1A' }}>
            Recommended colors
          </Typography>
          <Box className={`w-fit mb-[6px] shrink-0 items-center gap-3 ml-auto ${isMobile ? 'flex' : 'hidden'}`}>
            <IconButton
              aria-label="Previous page"
              size="small"
              disableRipple
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
              sx={colorPagerArrowIconButtonSx}
            >
              <ArrowLeft
                fill={currentPage === 1 ? ARROW_ICON_DISABLED : ARROW_ICON_ENABLED}
              />
            </IconButton>
            <IconButton
              aria-label="Next page"
              size="small"
              disableRipple
              disabled={currentPage === 2}
              onClick={() => setCurrentPage(2)}
              sx={colorPagerArrowRightIconButtonSx}
            >
              <ArrowRight
                fill={currentPage === 2 ? ARROW_ICON_DISABLED : ARROW_ICON_ENABLED}
              />
            </IconButton>
          </Box>
        </Box>
        <Box className={`flex flex-wrap gap-x-[14px] gap-y-[22px] xl:grid xl:grid-cols-4 ${isMobile ? 'mt-[4px]' : 'mt-[8px]'}`}>
          {(isMobile? templateColors.slice((currentPage - 1) * 6, currentPage * PAGE_LIMIT) :templateColors).map((colors, index) => {
            if(isMobile)
              index = ((currentPage - 1) * 6 + index)
            
            const {
              styles: { preColor: palette },
            } = colors
            return (
              <ColorPill
                key={index}
                primary={templateVersion === 'v2' ? palette[selectedMapping.primary] : palette.brand}
                secondary={templateVersion === 'v2' ? palette[selectedMapping.secondary] :palette.altBrand}
                text={templateVersion === 'v2' ? palette[selectedMapping.tertiary] : palette.text}
                selected={!showCustomColors && colors.title === selectedPalette?.title}
                onChange={() => {
                  handleChange(index)
                }}
              />
            )
          })}
        </Box>
      </Box>
      <Box
        component={'section'}
        className='mt-7'
      >
        <Typography variant='h5' sx={{ color: '#1A1A1A' }}>Custom color</Typography>
        {!showCustomColors && (
          <Box
            className='mt-2 w-fit border border-dashed border-2'
            sx={{
              borderRadius: '9999px',
              overflow: 'hidden',
              background: '#F5F5F5',
              borderColor: '#C0C4CF',
              ':hover': {
                borderColor: '#50CFF1',
                backgroundColor: theme.palette.primary.light,
              },
            }}
          >
            <Button
              disableFocusRipple
              disableRippleh
              variant='text'
              startIcon={
                <img
                  src={colorPaletteIconUrl}
                  alt=""
                  width={12}
                  height={12}
                  style={{ display: 'block' }}
                />
              }
              onClick={() => {
                setShowCustomColors((prev) => !prev)
              }}
              sx={{
                color: '#000',
                borderRadius: '9999px',
                px: '14px',
                py: '2px',
                textTransform: 'none',
                minWidth: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Typography className='underline underline-offset-2' sx={{ fontSize: '12px', fontWeight: 500 }}>
                Custom
              </Typography>
            </Button>
          </Box>
        )}
        {showCustomColors && (
          <Box className='mt-5'>
            <ColorPill
              disabled
              primary={templateVersion === 'v2' ? paletteColors[selectedMapping.primary] : paletteColors.brand}
              secondary={templateVersion === 'v2' ? paletteColors[selectedMapping.secondary] : paletteColors.altBrand}
              text={templateVersion === 'v2' ? paletteColors[selectedMapping.tertiary] : paletteColors.text}
              selected
            />
            <List className='!mt-5'>
              {listColors.map((color, index) => (
                <ListItem
                  key={index}
                  className='!p-0 !pb-2 mt-2 border-b last:border-0'
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant='h6' sx={{ color: '#1A1A1A' }}>
                      {color.name}
                    </Typography>
                    <Typography
                      variant='body2'
                      className='w-3/4 md:w-fit'
                      sx={{ color: '#1A1A1A' }}
                    >
                      {color.description}
                    </Typography>
                  </Box>
                  <Typography
                    textTransform={'uppercase'}
                    variant='body2'
                    fontFamily={'monospace'}
                    sx={{ marginRight: 2, color: colors.grey[600] }}
                  >
                    {rgbToHex(color.background)}
                  </Typography>
                  <Box
                    className='flex justify-center items-center border border-gray-400'
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: color.background,
                      borderRadius: 1,
                    }}
                  >
                    <IconButton
                      aria-label='edit'
                      size={'small'}
                      sx={{
                        color: theme.palette.getContrastText(color.background),
                      }}
                      onClick={(e) => {
                        setPrevColorPalette(selectedPalette)
                        setColorPickerFor(color.bind)
                        setColorPickerAnchor(e.currentTarget)
                      }}
                    >
                      <ColorizeIcon fontSize={'12px'} />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        <Popover
          className='ml-0 md:ml-5'
          open={!!colorPickerFor}
          anchorEl={colorPickerAnchor}
          onClose={() => {
            setSelectedPalette(prevColorPalette)
            setColorPickerFor(null)
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            bgcolor: { xs: '#1E1E1E', sm: 'transparent' }
          }}
          slotProps={{
            paper: {
              sx: {
                maxWidth: '100% !important',
                maxHeight: '100% !important',
                '@media (max-width: 600px)': {
                  left: '0px !important',
                },
              },
            }
          }}
        >
          <ColorPicker
            color={rgba(selectedPalette?.styles?.preColor[colorPickerFor])}
            onClose={() => {
              setColorPickerFor(null)
            }}
            onCancel={() => {
              setSelectedPalette(prevColorPalette)
            }}
            onChange={(newColor) => {
              const newPalette = JSON.parse(JSON.stringify(selectedPalette))
              newPalette.title = 'Custom'
              newPalette.templateIndex = 'custom'
              newPalette.styles.preColor[colorPickerFor] = hexToRgb(newColor)
                .replace('rgb(', '')
                .replace(')', '')
                .trim()
              setSelectedPalette(newPalette)
            }}
            currentColors={getPaletteColors(prevColorPalette)}
          />
        </Popover>
      </Box>
    </Box>
  )
}

const ColorPill = ({
  primary,
  secondary,
  text,
  onChange,
  selected,
  ...rest
}) => {
  return (
    <Button
      {...rest}
      className='!flex !rounded-3xl !overflow-hidden !w-[90px] !h-[24px] !p-0'
      onClick={onChange}
      sx={{
        borderColor: selected
          ? theme.palette.secondary.main
          : '#C0C4CF',
        borderWidth: selected ? 2.5 : 1,
        borderStyle: 'solid',
        outline: selected ? '1px solid white' : 'none',
        outlineOffset: '-3px'
      }}
    >
      <Box
        sx={{ background: rgba(primary) }}
        className='h-[24px] flex-1 border-r border-gray-400'
      />
      <Box
        sx={{ background: rgba(secondary) }}
        className='h-[24px] flex-1 border-r border-gray-400'
      />
      <Box
        sx={{ background: rgba(text) }}
        className='h-[24px] flex-1'
      />
    </Button>
  )
}

ColorPill.propTypes = {
  primary: PropTypes.string.isRequired,
  secondary: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  selected: PropTypes.bool,
}

export default WizardStep2
